import { useState } from 'react';
import { adminApi } from '@/lib/admin';
import { SectionCard } from '@/pages/admin/components/UI';

function PasswordInput({
  label,
  value,
  placeholder,
  onChange,
  autoComplete,
}: {
  label: string;
  value: string;
  placeholder: string;
  onChange: (v: string) => void;
  autoComplete?: string;
}) {
  const [show, setShow] = useState(false);
  return (
    <div>
      <label className="block text-brown-900 text-sm font-medium mb-1.5">{label}</label>
      <div className="flex items-center border border-brown-200 rounded-lg px-3 py-2.5 focus-within:border-gold-400 focus-within:ring-1 focus-within:ring-gold-400 transition-all">
        <input
          type={show ? 'text' : 'password'}
          autoComplete={autoComplete}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="w-full bg-transparent text-sm text-brown-900 placeholder:text-brown-400 outline-none"
        />
        <button
          type="button"
          onClick={() => setShow(!show)}
          className="ml-2 w-5 h-5 flex items-center justify-center text-brown-400 hover:text-brown-700 transition-colors cursor-pointer flex-shrink-0"
          tabIndex={-1}
        >
          <i className={`${show ? 'ri-eye-line' : 'ri-eye-off-line'} text-lg`}></i>
        </button>
      </div>
    </div>
  );
}

export default function AdminPassword() {
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    setSuccess(false);

    if (!oldPassword) {
      setFormError('请输入旧密码');
      return;
    }
    if (newPassword.length < 6) {
      setFormError('新密码长度至少 6 位');
      return;
    }
    if (newPassword !== confirmPassword) {
      setFormError('两次输入的新密码不一致');
      return;
    }

    setSubmitting(true);
    try {
      await adminApi.changePassword(oldPassword, newPassword);
      setSuccess(true);
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setFormError(err instanceof Error ? err.message : '修改失败，请重试');
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit =
    oldPassword.length > 0 && newPassword.length > 0 && confirmPassword.length > 0;

  return (
    <div className="space-y-5 max-w-xl">
      <SectionCard title="修改管理员密码">
        <form onSubmit={handleSubmit} className="p-4 md:p-5 space-y-5" noValidate>
          <p className="text-brown-400 text-xs">
            修改后请妥善保管新密码。密码通过加密方式存储，登录时用于验证后台管理员身份。
          </p>

          <PasswordInput
            label="旧密码"
            value={oldPassword}
            placeholder="请输入当前密码"
            onChange={(v) => {
              setOldPassword(v);
              if (formError) setFormError('');
            }}
            autoComplete="current-password"
          />

          <PasswordInput
            label="新密码"
            value={newPassword}
            placeholder="至少 6 位"
            onChange={(v) => {
              setNewPassword(v);
              if (formError) setFormError('');
            }}
            autoComplete="new-password"
          />

          <PasswordInput
            label="确认新密码"
            value={confirmPassword}
            placeholder="再次输入新密码"
            onChange={(v) => {
              setConfirmPassword(v);
              if (formError) setFormError('');
            }}
            autoComplete="new-password"
          />

          {formError && (
            <div className="flex items-center gap-2 bg-red-50 border border-red-100 rounded-lg px-3 py-2.5">
              <span className="w-4 h-4 flex items-center justify-center text-red-500 flex-shrink-0">
                <i className="ri-error-warning-line text-sm"></i>
              </span>
              <span className="text-xs text-red-600">{formError}</span>
            </div>
          )}

          {success && (
            <div className="flex items-center gap-2 bg-green-50 border border-green-100 rounded-lg px-3 py-2.5">
              <span className="w-4 h-4 flex items-center justify-center text-green-600 flex-shrink-0">
                <i className="ri-checkbox-circle-line text-sm"></i>
              </span>
              <span className="text-xs text-green-700">密码修改成功</span>
            </div>
          )}

          <div className="pt-1">
            <button
              type="submit"
              disabled={submitting || !canSubmit}
              className="w-full sm:w-auto px-8 py-2.5 rounded-lg bg-gold-500 text-brown-950 text-sm font-medium cursor-pointer whitespace-nowrap hover:bg-gold-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {submitting ? (
                <>
                  <span className="w-4 h-4 border-2 border-brown-950/30 border-t-brown-950 rounded-full animate-spin"></span>
                  保存中...
                </>
              ) : (
                <>
                  <i className="ri-lock-line"></i>
                  确认修改
                </>
              )}
            </button>
          </div>
        </form>
      </SectionCard>
    </div>
  );
}